import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxIcon,
  ComboboxItem,
  ComboboxList,
  ComboboxSearchInput,
  ComboboxTrigger,
  ComboboxValue,
} from '@/components/ui/combobox';
import { useDebounce } from '@/hooks/useDebounce';
import {
  PCMP_LOOKUP_MAX_CHARS,
  PCMP_LOOKUP_MIN_CHARS,
  pcmpLookupUsersQueryOptions,
} from '../../api/pcmpLookups.api';
import type { PcmpPickedUser } from '../../hooks/usePcmpMoReportFilters';
import type { PcmpLookupUserKind } from '../../types/pcmpLookups.types';
import { PcmpStatusPill } from '../PcmpProposalDetail/PcmpStatusPill';

interface PcmpUserPickerProps {
  /** Search the MOs, or the people MOs report to. */
  kind: PcmpLookupUserKind;
  value: PcmpPickedUser | null;
  onChange: (value: PcmpPickedUser | null) => void;
  placeholder: string;
  id?: string;
}

interface UserOption extends PcmpPickedUser {
  detail: string;
}

// A single-select does not deselect on a second click: this entry clears the pick.
const CLEAR_ID = 0;

/** A user who left is still listed so their proposals stay reachable (LDS-799). */
function UserName({ user }: { user: PcmpPickedUser }) {
  return (
    <span className="flex min-w-0 items-center gap-1.5">
      <span className="truncate">{user.name}</span>
      {user.isActive === false ? (
        <PcmpStatusPill tone="danger" className="px-1.5 py-0 text-[11px]">
          Inactive
        </PcmpStatusPill>
      ) : null}
    </span>
  );
}

/**
 * Type-ahead over the PCMP lookups (at most 20 matches, by name, HR id or
 * username). Admins find anyone; everyone else finds their downline.
 */
export function PcmpUserPicker({ kind, value, onChange, placeholder, id }: PcmpUserPickerProps) {
  const [search, setSearch] = useState('');
  const typed = search.trim();
  const term = useDebounce(typed);
  const isTooShort = typed.length < PCMP_LOOKUP_MIN_CHARS;
  // The results belong to what is typed only once the debounce has caught up.
  const isCurrent = term === typed;

  const { data, isFetching, isPlaceholderData, isError, refetch } = useQuery(
    pcmpLookupUsersQueryOptions(kind, term),
  );
  const isSearching = !isTooShort && (!isCurrent || isFetching);

  const options = useMemo<UserOption[]>(
    () =>
      isTooShort || !isCurrent || isPlaceholderData
        ? []
        : (data?.map((user) => ({
            id: user.id,
            name: user.text,
            isActive: user.isActive,
            detail: [user.designation, user.department].filter(Boolean).join(' · '),
          })) ?? []),
    [data, isTooShort, isCurrent, isPlaceholderData],
  );

  const items = useMemo<UserOption[]>(
    () =>
      value
        ? [{ id: CLEAR_ID, name: placeholder, detail: 'Clear the selection' }, ...options]
        : options,
    [value, options, placeholder],
  );

  return (
    <Combobox<PcmpPickedUser | null>
      items={items}
      value={value}
      onValueChange={(next) =>
        onChange(
          next && next.id !== CLEAR_ID
            ? { id: next.id, name: next.name, isActive: next.isActive }
            : null,
        )
      }
      inputValue={search}
      onInputValueChange={setSearch}
      filter={null}
      itemToStringLabel={(item) => item?.name ?? ''}
      isItemEqualToValue={(item, selected) => item?.id === selected?.id}
    >
      <ComboboxTrigger id={id} className="h-10">
        <ComboboxValue>
          {(selected: PcmpPickedUser | null) =>
            selected ? <UserName user={selected} /> : placeholder
          }
        </ComboboxValue>
        <ComboboxIcon />
      </ComboboxTrigger>
      <ComboboxContent>
        <ComboboxSearchInput
          placeholder="Search by name or HR id..."
          maxLength={PCMP_LOOKUP_MAX_CHARS}
        />
        <ComboboxList>
          {items.map((option) => (
            <ComboboxItem key={option.id} value={option}>
              <div className="flex min-w-0 flex-col">
                <UserName user={option} />
                {option.detail ? (
                  <span className="text-xs text-muted-foreground">{option.detail}</span>
                ) : null}
              </div>
            </ComboboxItem>
          ))}
        </ComboboxList>
        {isSearching ? (
          <div className="flex justify-center py-2">
            <Loader2 className="size-4 animate-spin text-muted-foreground" />
          </div>
        ) : null}
        {isTooShort ? (
          <p className="px-3 py-2 text-xs text-muted-foreground">
            Type at least {PCMP_LOOKUP_MIN_CHARS} characters to search.
          </p>
        ) : isError && !isSearching ? (
          <div className="flex items-center justify-between gap-2 px-3 py-2 text-xs text-destructive">
            <span>Could not search users.</span>
            <Button type="button" variant="ghost" size="sm" onClick={() => void refetch()}>
              Retry
            </Button>
          </div>
        ) : isSearching ? null : (
          <ComboboxEmpty>No users found.</ComboboxEmpty>
        )}
      </ComboboxContent>
    </Combobox>
  );
}
